import { useNavigate } from "react-router-dom";
import { Table } from "../../../Components/Table/Table";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import React, { useMemo, useState, useEffect } from "react";
import {
  getAllEmployees,
  deleteEmployee,
  exportEmployees,
  toggleEmployeeStatus,
} from "../../../api/register";
import toast from "react-hot-toast";

const Employees = () => {
  const navigate = useNavigate();

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [selectedDate, setSelectedDate] = useState("");
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageno, setPageno] = useState(1);
  const [togglingId, setTogglingId] = useState(null);
  const [activeFilter, setActiveFilter] = useState("All");

  // 🔥 NEW: refresh trigger
  const [refresh, setRefresh] = useState(false);

  // ✅ FETCH DATA
  const fetchEmployees = async () => {
    try {
      setLoading(true);

      const params = {
        search,
        date: selectedDate,
        status: statusFilter,
        page: pageno,
        _t: new Date().getTime(), // 🔥 cache break
      };

      if (activeFilter !== "All") {
        params.isActive = activeFilter === "Active" ? "true" : "false";
      }

      console.log("FINAL PARAMS:", params);

      const res = await getAllEmployees(params);

      setEmployees(res.data.employees || []);
    } catch (error) {
      toast.error("Failed to fetch employees");
      setEmployees([]);
    } finally {
      setLoading(false);
    }
  };

  // ✅ AUTO FETCH
  useEffect(() => {
    fetchEmployees();
  }, [search, selectedDate, statusFilter, activeFilter, pageno, refresh]);

  // ✅ EXPORT
  const handleExport = async () => {
    try {
      const res = await exportEmployees({
        search,
        date: selectedDate,
        status: statusFilter,
      });

      const blob = new Blob([res.data]);
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = "employees.xlsx";
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast.success("Export successful");
    } catch (error) {
      toast.error("Export failed");
    }
  };

  // ✅ TOGGLE STATUS
const handleToggleStatus = async (id) => {
  try {
    setTogglingId(id);

    await toggleEmployeeStatus(id);

    // ✅ INSTANT UI UPDATE (no wait for API refetch)
    setEmployees((prev) =>
      prev.map((emp) =>
        emp._id === id
          ? { ...emp, isActive: !emp.isActive }
          : emp
      )
    );

    toast.success("Status updated");

  } catch (error) {
    toast.error("Toggle failed");
  } finally {
    setTogglingId(null);
  }
};

  // ✅ DELETE
const handleDelete = async (id) => {
  if (!window.confirm("Are you sure you want to delete?")) return;

  try {
    await deleteEmployee(id);

    toast.success("Employee deleted successfully");

    // 🔥 DIRECT REFRESH
    await fetchEmployees();

  } catch (error) {
    toast.error("Delete failed");
  }
};

  // ✅ TABLE DATA
  const tableData = useMemo(() => {
    return employees.map((emp) => ({
      id: emp._id,
      employeeid: emp.employeeId,
      name: `${emp.firstName || ""} ${emp.lastName || ""}`,
      email: emp.personalmailid,
      mobile: emp.mobilenumber,
      department: emp.designation,
      status: emp.status || "pending",
      isActive: emp.isActive,
    }));
  }, [employees]);

  // ✅ COLUMNS
  const columns = useMemo(
    () => [
      {
        Header: "S.No",
        Cell: ({ row }) => <span>{row.index + 1}</span>,
      },
      {
        Header: "Employee ID",
        accessor: "employeeid",
      },
      {
        Header: "Name",
        accessor: "name",
      },
      {
        Header: "Email",
        accessor: "email",
      },
      {
        Header: "Mobile",
        accessor: "mobile",
      },
      {
        Header: "Department",
        accessor: "department",
      },
      {
        Header: "Status",
        accessor: "status",
        Cell: ({ value }) => (
          <span className={`status-badge ${value?.toLowerCase()}`}>
            {value}
          </span>
        ),
      },
      {
        Header: "Actions",
        Cell: ({ row }) => (
          <div className="action-icons">
            <EditIcon
              onClick={() => navigate(`/employee/edit/${row.original.id}`)}
              style={{ cursor: "pointer" }}
            />

            <DeleteIcon
              onClick={() => handleDelete(row.original.id)}
              style={{ cursor: "pointer", color: "red" }}
            />

            <RemoveRedEyeIcon
              onClick={() => navigate(`/register-view/${row.original.id}`)}
              style={{ cursor: "pointer" }}
            />
          </div>
        ),
      },
      {
        Header: "Active",
        Cell: ({ row }) => {
          const isActive = row.original.isActive;
          const isLoading = togglingId === row.original.id;

          return (
            <button
              onClick={() => handleToggleStatus(row.original.id)}
              disabled={isLoading}
              className={`emp-status-btn ${
                isActive ? "emp-active" : "emp-inactive"
              } ${isLoading ? "emp-loading" : ""}`}
            >
              {isLoading
                ? "Updating..."
                : isActive
                ? "Active"
                : "Inactive"}
            </button>
          );
        },
      },
    ],
    [navigate, togglingId] // 🔥 FIXED dependency
  );

  return (
    <div className="container-admin">
      {/* TOP SECTION */}
      <div className="top-section">
        <div className="export_button">
          <button onClick={handleExport}>Export</button>
        </div>

        <div className="filters">
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
          />

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="All">All</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>

          <input
            type="text"
            placeholder="Search employee..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

        <select
  value={activeFilter}
  onChange={(e) => {
    console.log("ACTIVE FILTER:", e.target.value); // 🔥 add this
    setActiveFilter(e.target.value);
  }}
>
            <option value="All">All</option>
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
          </select>

          <button
            className="add-btn"
            onClick={() => navigate("/employee/add")}
          >
            <AddIcon /> Add Employee
          </button>
        </div>
      </div>

      {/* TABLE */}
      <div className="mt-3">
        <Table
          columns={columns}
          data={tableData}
          show={true}
          pageno={pageno}
          setPageno={setPageno}
        />
      </div>
    </div>
  );
};

export default Employees;